import React from "react";
import { getStockTopUpHeader, listStockTopUpLines } from "../../data/stockTopUp";
import {
  lineLabel,
  SECTION_LABELS,
  SECTION_ORDER,
  sectionUnitLabel,
} from "../../services/stockTopUpForm";
import { Card } from "../../utils/ui.jsx";
import { TopUpShelterHeader } from "./TopUpShelterHeader.jsx";

const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#607D79",
  fontWeight: 700,
  padding: "8px 10px",
  borderBottom: "1px solid #ECEFF1",
};
const itemHead = { ...th, minWidth: 220 };
const qtyHead = { ...th, textAlign: "center", padding: "8px 24px" };
const cell = { fontSize: 13, color: "#1A2E2B", padding: "8px 10px" };
const qtyCell = { ...cell, textAlign: "center", padding: "8px 24px" };
// The pack-unit chip (socks / bulk) shown beside the item — the ordering unit, display-only.
const unitChip = {
  display: "inline-block",
  marginLeft: 8,
  fontSize: 12,
  fontWeight: 600,
  color: "#00695C",
  background: "#FFF4D6",
  border: "1px solid #EAC44E",
  borderRadius: 5,
  padding: "1px 7px",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

// A section's outcome from its lines (approved / declined / pending).
function decisionOf(lines, section) {
  const sl = lines.filter((l) => l.section === section);
  const decided = sl.find((l) => l.approval_status && l.approval_status !== "pending");
  return decided ? decided.approval_status : "pending";
}
function tabBg(dec, active) {
  if (active) return "var(--color-zephyr-teal)";
  if (dec === "approved") return "#E8F5E9";
  if (dec === "declined") return "#FFEBEE";
  return "#fff";
}

// Read-only view of a submitted Stock Top-up (shelter + staff), shown in RequestDetail
// when req.requestType === 'stock_top_up'. Mirrors the reviewer's tabbed layout (a tab
// per section) so the service sees its order the same way Zephyr reviews it. The
// Approved column appears once any line has been decided (after Confirm & Send).
//
// The TopUpShelterHeader (General Information | Last Comment | Summary) sits above the
// tabs so a top-up "looks like" a standard request to the DFV Service. The comment
// props come from RequestDetail (which owns the notes state + the CommentPanel);
// isShelterUser gates the Last Comment card (a Zephyr viewer uses the title bar).
export default function TopUpReadView({
  req,
  isShelterUser = true,
  notes = [],
  setShowCommentPanel,
  hasUnread = false,
  onCommentsOpened,
}) {
  const [lines, setLines] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [activeSection, setActiveSection] = React.useState(null);

  React.useEffect(
    function () {
      const id = req?.id;
      if (id == null) return undefined;
      let alive = true;
      Promise.all([listStockTopUpLines(id), getStockTopUpHeader(id)]).then(function ([ls, hdr]) {
        if (!alive) return;
        setLines(ls);
        setNote(hdr?.note || "");
        const present = SECTION_ORDER.filter((s) => ls.some((l) => l.section === s));
        setActiveSection(present[0] || null);
      });
      return function () {
        alive = false;
      };
    },
    [req?.id]
  );

  if (lines === null) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#8AA5A1" }}>Loading top-up…</div>
      </Card>
    );
  }
  if (lines.length === 0) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#8AA5A1" }}>No items on this top-up.</div>
      </Card>
    );
  }

  const sections = SECTION_ORDER.filter((s) => lines.some((l) => l.section === s));
  const sectionLines = lines.filter((l) => l.section === activeSection);
  const anyDecided = lines.some((l) => l.approval_status && l.approval_status !== "pending");

  return (
    <div>
      {/* Standard-request-style header cards (General Info | Last Comment | Summary). */}
      <TopUpShelterHeader
        req={req}
        sectionLabels={sections.map((s) => SECTION_LABELS[s])}
        isShelterUser={isShelterUser}
        notes={notes}
        setShowCommentPanel={setShowCommentPanel}
        hasUnread={hasUnread}
        onCommentsOpened={onCommentsOpened}
      />

      {/* Section tabs (read-only) — tinted by the outcome. */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {sections.map((s) => {
          const active = activeSection === s;
          const dec = decisionOf(lines, s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => setActiveSection(s)}
              style={{
                border: "1px solid #CFE0DE",
                borderRadius: 8,
                padding: "6px 12px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
                background: tabBg(dec, active),
                color: active ? "#fff" : "#1A2E2B",
              }}
            >
              {SECTION_LABELS[s]}
              {dec !== "pending" && <span> {dec === "approved" ? "✓" : "✗"}</span>}
            </button>
          );
        })}
      </div>

      <Card>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={itemHead}>Item</th>
              <th style={qtyHead}>On hand</th>
              <th style={qtyHead}>Requested</th>
              {anyDecided && <th style={qtyHead}>Approved</th>}
            </tr>
          </thead>
          <tbody>
            {sectionLines.map((l) => {
              const isReimb = l.amount != null;
              const declined = l.approval_status === "declined";
              return (
                <tr
                  key={l.id}
                  style={{ borderTop: "1px solid #EEF3F2", opacity: declined ? 0.55 : 1 }}
                >
                  <td style={cell}>
                    {lineLabel(l)}
                    {sectionUnitLabel(l.section) ? (
                      <span style={unitChip}>{sectionUnitLabel(l.section)}</span>
                    ) : null}
                  </td>
                  <td style={qtyCell}>{l.qty_on_hand ?? "—"}</td>
                  <td style={qtyCell}>{isReimb ? `$${l.amount}` : (l.qty_required ?? "—")}</td>
                  {anyDecided && (
                    <td style={qtyCell}>
                      {declined ? "Declined" : isReimb ? `$${l.amount}` : (l.approved_qty ?? "—")}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {note && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Other / Notes</div>
          <div style={{ fontSize: 13, color: "#1A2E2B", whiteSpace: "pre-wrap" }}>{note}</div>
        </Card>
      )}
    </div>
  );
}
